import React, { useCallback, useRef, useState } from 'react';
import {
  GoogleMap,
  Marker,
  useJsApiLoader,
  Autocomplete,
} from '@react-google-maps/api';

// Value carried by the picker — an address string plus its coordinates. Coords
// are null until the user picks a place / drops a pin (the Create Shipment form
// treats them as required before submit).
export interface LocationValue {
  address: string;
  lat: number | null;
  long: number | null;
}

interface LocationPickerProps {
  value: LocationValue;
  onChange: (next: LocationValue) => void;
  /** Map fallback center when the value has no coords yet (defaults to Jakarta). */
  defaultCenter?: { lat: number; lng: number };
  placeholder?: string;
}

// Stable references so useJsApiLoader / the map don't re-init on every render
// (the loader warns loudly if `libraries` is a new array each render).
const MAPS_LIBRARIES: 'places'[] = ['places'];
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? '';
const DEFAULT_CENTER = { lat: -6.2088, lng: 106.8456 }; // Jakarta
const MAP_CONTAINER_STYLE = { width: '100%', height: '260px', borderRadius: 12 };

const manualInputCls =
  'w-full rounded-lg border border-stroke-sub-300 bg-bg-white-0 px-3 py-2 text-sm text-text-strong-950 outline-none focus:border-primary';

// Parse a hand-typed coordinate. Accepts a comma OR a period decimal separator
// (Indonesian keyboards default to comma), and returns null for empty or still
// in-progress input (`''`, `-`, `.`, `-.`) so typing a negative/decimal value
// isn't interrupted.
const parseCoord = (raw: string): number | null => {
  const t = raw.trim().replace(',', '.');
  if (t === '' || t === '-' || t === '.' || t === '-.') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

// Manual address + coordinate entry — used both as the no-API-key fallback and
// as an "advanced" way to type exact coordinates.
const ManualLocationFields: React.FC<
  LocationPickerProps & { note?: string }
> = ({ value, onChange, placeholder, note }) => {
  // Keep the raw coordinate TEXT locally so partial entries (`-`, `-6.`) and a
  // comma decimal (`-6,1231`) type without the field fighting back. The numeric
  // value is lifted to the parent only when the text parses to a finite number.
  const [latStr, setLatStr] = useState(
    value.lat != null ? String(value.lat) : ''
  );
  const [lngStr, setLngStr] = useState(
    value.long != null ? String(value.long) : ''
  );

  const handleCoord = (raw: string, key: 'lat' | 'long') => {
    if (key === 'lat') setLatStr(raw);
    else setLngStr(raw);
    onChange({ ...value, [key]: parseCoord(raw) });
  };

  return (
    <div className="space-y-3">
      {note && <p className="text-[11px] text-text-soft-400">{note}</p>}
      <input
        value={value.address}
        onChange={(e) => onChange({ ...value, address: e.target.value })}
        placeholder={placeholder ?? 'Alamat tujuan lengkap'}
        className={manualInputCls}
      />
      <div className="grid grid-cols-2 gap-3">
        <input
          value={latStr}
          inputMode="decimal"
          onChange={(e) => handleCoord(e.target.value, 'lat')}
          placeholder="Latitude (mis. -6,2088)"
          className={manualInputCls}
        />
        <input
          value={lngStr}
          inputMode="decimal"
          onChange={(e) => handleCoord(e.target.value, 'long')}
          placeholder="Longitude (mis. 106,8456)"
          className={manualInputCls}
        />
      </div>
    </div>
  );
};

const LocationPicker: React.FC<LocationPickerProps> = (props) => {
  // No API key configured → degrade to manual address + coordinate entry so the
  // feature still works in dev / environments without a Maps key.
  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <ManualLocationFields
        {...props}
        note="Peta belum aktif — masukkan alamat & koordinat tujuan secara manual."
      />
    );
  }

  return <MapLocationPicker {...props} key="map" />;
};

// Split out so the hooks (useJsApiLoader etc.) only run when a key exists.
const MapLocationPicker: React.FC<LocationPickerProps> = ({
  value,
  onChange,
  defaultCenter,
  placeholder,
}) => {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: MAPS_LIBRARIES,
  });

  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);
  const [search, setSearch] = useState('');

  const center =
    value.lat != null && value.long != null
      ? { lat: value.lat, lng: value.long }
      : (defaultCenter ?? DEFAULT_CENTER);

  // Reverse-geocode a dropped/dragged pin into a human address (best-effort;
  // keeps the previous address if the lookup fails or is disabled).
  const reverseGeocode = useCallback(
    (lat: number, lng: number) => {
      if (!geocoderRef.current) {
        try {
          geocoderRef.current = new google.maps.Geocoder();
        } catch {
          onChange({ address: value.address, lat, long: lng });
          return;
        }
      }
      geocoderRef.current.geocode({ location: { lat, lng } }, (results, status) => {
        const address =
          status === 'OK' && results && results[0]
            ? results[0].formatted_address
            : value.address;
        onChange({ address, lat, long: lng });
      });
    },
    [onChange, value.address]
  );

  const handlePlaceChanged = () => {
    const place = autocompleteRef.current?.getPlace();
    if (!place?.geometry?.location) return;
    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    onChange({
      address: place.formatted_address ?? place.name ?? value.address,
      lat,
      long: lng,
    });
    setSearch(place.formatted_address ?? place.name ?? '');
  };

  if (loadError) {
    return (
      <ManualLocationFields
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        note="Gagal memuat Google Maps. Masukkan alamat & koordinat manual."
      />
    );
  }

  if (!isLoaded) {
    return (
      <div className="flex h-[260px] items-center justify-center rounded-xl border border-stroke-sub-300 bg-bg-weak-50 text-sm text-text-soft-400">
        <i className="ri-loader-4-line mr-2 animate-spin" /> Memuat peta…
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Autocomplete
        onLoad={(ac) => (autocompleteRef.current = ac)}
        onPlaceChanged={handlePlaceChanged}
        options={{ componentRestrictions: { country: 'id' } }}
      >
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-text-soft-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={placeholder ?? 'Cari alamat atau titik lokasi (POI)'}
            className="w-full rounded-lg border border-stroke-sub-300 bg-bg-white-0 py-2 pl-9 pr-3 text-sm text-text-strong-950 outline-none focus:border-primary"
          />
        </div>
      </Autocomplete>

      <GoogleMap
        mapContainerStyle={MAP_CONTAINER_STYLE}
        center={center}
        zoom={value.lat != null ? 16 : 12}
        options={{
          disableDefaultUI: true,
          zoomControl: true,
          clickableIcons: false,
        }}
        onClick={(e) => {
          if (e.latLng) reverseGeocode(e.latLng.lat(), e.latLng.lng());
        }}
      >
        {value.lat != null && value.long != null && (
          <Marker
            position={{ lat: value.lat, lng: value.long }}
            draggable
            onDragEnd={(e) => {
              if (e.latLng) reverseGeocode(e.latLng.lat(), e.latLng.lng());
            }}
          />
        )}
      </GoogleMap>

      {/* Selected address (editable) + resolved coordinates for confirmation. */}
      <input
        value={value.address}
        onChange={(e) => onChange({ ...value, address: e.target.value })}
        placeholder="Alamat tujuan (otomatis dari peta — bisa diedit)"
        className="w-full rounded-lg border border-stroke-sub-300 bg-bg-white-0 px-3 py-2 text-sm text-text-strong-950 outline-none focus:border-primary"
      />
      <p className="text-[11px] text-text-soft-400">
        {value.lat != null && value.long != null ? (
          <>
            Koordinat:{' '}
            <span className="font-mono">
              {value.lat.toFixed(6)}, {value.long.toFixed(6)}
            </span>{' '}
            — klik peta atau geser pin untuk menyesuaikan.
          </>
        ) : (
          'Cari alamat, atau klik peta untuk menaruh pin tujuan.'
        )}
      </p>
    </div>
  );
};

export default LocationPicker;
